import React, { useState, useEffect, useRef } from 'react';
import usePortalAuth from '../../auth/usePortalAuth';
import './FirstLogin.css';
import useChangePageTitle from '../Dashboard/CustomHooks/useChangePageTitle';
import EmailLogin from '../EmailLogin/EmailLogin';
import LoggedOut from './LoggedOut';
import { appendLogoutTrace } from '../../utils/logoutTrace';
import { getBrandedLoginRouteFromSearch } from './cidLoginRouting';
const LOGOUT_MARKER_KEY = 'pcPortalLoggedOut';
const LOGOUT_MARKER_MAX_AGE_MS = 10 * 60 * 1000;
const LOGIN_LOADING_FALLBACK_MS = 3500;

const consumeLogoutMarker = () => {
  try {
    const rawMarker =
      sessionStorage.getItem(LOGOUT_MARKER_KEY) ||
      localStorage.getItem(LOGOUT_MARKER_KEY);
    if (!rawMarker) {
      return false;
    }
    if (rawMarker === '1') {
      sessionStorage.removeItem(LOGOUT_MARKER_KEY);
      localStorage.removeItem(LOGOUT_MARKER_KEY);
      return true;
    }

    let markerTimestamp = 0;
    try {
      const parsedMarker = JSON.parse(rawMarker);
      markerTimestamp = Number(parsedMarker?.at || 0);
    } catch (parseError) {
      markerTimestamp = Number(rawMarker || 0);
    }

    sessionStorage.removeItem(LOGOUT_MARKER_KEY);
    localStorage.removeItem(LOGOUT_MARKER_KEY);

    if (!markerTimestamp) {
      return true;
    }

    return Date.now() - markerTimestamp <= LOGOUT_MARKER_MAX_AGE_MS;
  } catch (markerError) {
    return false;
  }
};

const LoginPortal = (props) => {
  useChangePageTitle('PortalLogin');
  const { isLoading, isAuthenticated, loginWithRedirect } = usePortalAuth();
  const [hasFreshLogoutMarker] = useState(() => consumeLogoutMarker());
  const hasTriggeredAccessTokenRedirect = useRef(false);
  const [isLoadingTimedOut, setIsLoadingTimedOut] = useState(false);
  // const [isDelayed, setIsDelayed] = useState(true);

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setIsDelayed(false);
  //   }, 3000);

  //   // Cleanup timer on unmount
  //   return () => clearTimeout(timer);
  // }, []);

  const searchParams = new URLSearchParams(window.location.search);
  const queryLoggedOut = searchParams.get('loggedOut') === '1';
  const brandedCidLoginRoute = getBrandedLoginRouteFromSearch(window.location.search);
  const justLoggedOut = queryLoggedOut || hasFreshLogoutMarker;
  const hasAccessTokenFragment = window.location.href.indexOf('#access_token=') > -1;
  const shouldShowEmailLogin =
    !justLoggedOut &&
    !isAuthenticated &&
    !hasAccessTokenFragment &&
    (!isLoading || isLoadingTimedOut);
  const renderBranch = justLoggedOut
    ? 'logged_out'
    : !isAuthenticated && hasAccessTokenFragment
      ? 'access_token_fragment_loading'
      : shouldShowEmailLogin
        ? 'email_login'
        : isAuthenticated
          ? 'authenticated_redirecting'
          : 'loading';

  useEffect(() => {
    appendLogoutTrace('login_portal_logout_marker_evaluated', {
      queryLoggedOut,
      hasFreshLogoutMarker,
      justLoggedOut,
    });
  }, [queryLoggedOut, hasFreshLogoutMarker, justLoggedOut]);

  useEffect(() => {
    if (!isLoading) {
      setIsLoadingTimedOut(false);
      return undefined;
    }

    const timeout = setTimeout(() => {
      setIsLoadingTimedOut(true);
      appendLogoutTrace('login_portal_loading_timeout_elapsed', {
        timeoutMs: LOGIN_LOADING_FALLBACK_MS,
      });
    }, LOGIN_LOADING_FALLBACK_MS);

    return () => clearTimeout(timeout);
  }, [isLoading]);

  useEffect(() => {
    appendLogoutTrace('login_portal_state_change', {
      isLoading: Boolean(isLoading),
      isLoadingTimedOut: Boolean(isLoadingTimedOut),
      isAuthenticated: Boolean(isAuthenticated),
      justLoggedOut: Boolean(justLoggedOut),
      hasFreshLogoutMarker: Boolean(hasFreshLogoutMarker),
      queryLoggedOut: Boolean(queryLoggedOut),
      hasAccessTokenFragment: Boolean(hasAccessTokenFragment),
    });
  }, [
    isLoading,
    isLoadingTimedOut,
    isAuthenticated,
    justLoggedOut,
    hasFreshLogoutMarker,
    queryLoggedOut,
    hasAccessTokenFragment,
  ]);

  useEffect(() => {
    appendLogoutTrace('login_portal_render_branch', {
      branch: renderBranch,
      isLoading: Boolean(isLoading),
      isLoadingTimedOut: Boolean(isLoadingTimedOut),
      isAuthenticated: Boolean(isAuthenticated),
      justLoggedOut: Boolean(justLoggedOut),
      hasAccessTokenFragment: Boolean(hasAccessTokenFragment),
    });
  }, [renderBranch, isLoading, isLoadingTimedOut, isAuthenticated, justLoggedOut, hasAccessTokenFragment]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !justLoggedOut && brandedCidLoginRoute) {
      appendLogoutTrace('login_portal_cid_redirect', { target: brandedCidLoginRoute });
      props.history.replace(brandedCidLoginRoute);
    }
  }, [isLoading, isAuthenticated, justLoggedOut, brandedCidLoginRoute, props.history]);

  useEffect(() => {
    if (window.location.search.includes('error=')) {
      appendLogoutTrace('login_portal_error_redirect', { target: '/Error' });
      props.history.replace('/Error');
    }
  }, [props.history]);

  useEffect(() => {
    if (!justLoggedOut && !isLoading && isAuthenticated) {
      appendLogoutTrace('login_portal_authenticated_redirect', { target: '/Dashboard/Callback' });
      props.history.replace('/Dashboard/Callback');
    }
  }, [justLoggedOut, isLoading, isAuthenticated, props.history]);

  useEffect(() => {
    if (
      !justLoggedOut &&
      !isLoading &&
      !isAuthenticated &&
      hasAccessTokenFragment &&
      !hasTriggeredAccessTokenRedirect.current
    ) {
      hasTriggeredAccessTokenRedirect.current = true;
      appendLogoutTrace('login_portal_access_token_redirect_triggered');
      loginWithRedirect();
    }
  }, [justLoggedOut, isLoading, isAuthenticated, hasAccessTokenFragment, loginWithRedirect]);

  const renderMainBody = () => {
    if (renderBranch === 'logged_out') {
      return <LoggedOut loginPath="/PortalSignIn" logoutMarkerKey={LOGOUT_MARKER_KEY} />;
    } else if (renderBranch === 'access_token_fragment_loading') {
      return <h3>Loading...</h3>;
    } else if (renderBranch === 'email_login') {
      return <EmailLogin />;
    } else if (renderBranch === 'authenticated_redirecting') {
      return <h3>Redirecting...</h3>;
    } else if (renderBranch === 'loading') {
      return <h3>Loading...</h3>;
    }

    return <p>Oops, something went wrong.</p>;
  };

  return (
    <div>
      {/* {isDelayed ? <h3>Loading...</h3> : renderMainBody()} */}
      {renderMainBody()}
    </div>
  );
};
export default LoginPortal;
